import { Path3D, Vector3 } from '@babylonjs/core/Maths/math';
import gsap from 'gsap';

import { Observable } from '../Tools/observable';
import { SystemCamera } from '../System/systemCamera';
import { GeometryVector } from '~src/Node/nodePosition';
import {
    ScrollPathLinear, EScrollPath, getScrollPathFromEnum
} from './scrollPathFunction';
import { PARTICLE_BY_LINE } from '~src/Element/elementLayerPath';

export enum EScrollAlignment {
    VERTICAL,
    HORIZONTAL,
    NORMAL, // Or SPATIAL, LONGITUDINAL
}

export enum ScrollEvent {
    Alignment,
    Progress,
    Start,
}

export interface IScroll {
    catchSpeed?: number,
    alignment?: EScrollAlignment,
    path?: EScrollPath,
}

export const DEFAULTSCROLLOPTIONS: IScroll = {
    catchSpeed: 1,
    alignment: EScrollAlignment.VERTICAL,
    path: EScrollPath.LINEAR,
};

const PARTICLE_START = 25;

const EMPTY_GEOMETRY: GeometryVector = { position: Vector3.Zero(), rotation: Vector3.Zero() };

export class ScrollPath extends Observable<ScrollEvent, number> {
    protected system: SystemCamera;

    protected browser: HTMLElement;

    private scrollingElement: Element;

    private ease: gsap.EaseFunction;

    constructor(system: SystemCamera, browser: HTMLElement) {
        super('Page Scroll');
        this.system = system;
        this.browser = browser;

        const easeString = 'power1.inOut';
        this.ease = gsap.parseEase(easeString);
    }

    protected _options: IScroll = DEFAULTSCROLLOPTIONS;

    protected scrollHeight: number;

    public checkHeight(): number {
        if (this.browser === document.body) this.scrollingElement = document.scrollingElement;
        else this.scrollingElement = this.browser;
        const browserHeight = this.scrollingElement.clientHeight;
        this.scrollHeight = this.scrollingElement.scrollHeight - browserHeight;

        return this.scrollHeight;
    }

    protected getScrollPercentage() {
        const top = this.scrollingElement.scrollTop;
        return (this.scrollHeight) ? top / this.scrollHeight : 0;
    }

    private _fullPath: Path3D;

    public get fullpath(): Path3D {
        return this._fullPath;
    }

    private updateFullPath(doNotUpdatePath?: boolean) {
        const newPath3D = [];
        const start = -PARTICLE_START;
        const end = this.pathLength + PARTICLE_BY_LINE;
        for (let i = start; i <= end; i++) {
            const geo = this.getScrollPahtWithAlignment(i);
            newPath3D.push(geo.position);
        }
        this._fullPath = new Path3D(newPath3D);
        if (!doNotUpdatePath) this.notify(ScrollEvent.Alignment, 0);
    }

    public alignmentVector = Vector3.One();

    protected _setAlignment(alignment: EScrollAlignment) {
        if (alignment === EScrollAlignment.VERTICAL) {
            // this.alignmentVector = new Vector3(1, 0, 0);
            this.alignmentVector = new Vector3(-Math.PI / 2, 0, 0);
        } else if (alignment === EScrollAlignment.HORIZONTAL) {
            // this.alignmentVector = new Vector3(0, 1, 0);
            this.alignmentVector = new Vector3(0, -Math.PI / 2, 0);
        } else if (alignment === EScrollAlignment.NORMAL) {
            this.alignmentVector = new Vector3(0, 0, 0);
        }
        this.updateFullPath();
    }

    private scrollPathFunction = ScrollPathLinear;

    public _setPath(path: EScrollPath) {
        const scrollPath = getScrollPathFromEnum(path);
        this.scrollPathFunction = scrollPath.fn;
        this.updateFullPath();
    }

    private pathLength = 100;

    public setPathLength(length: number, doNotUpdatePath?: boolean) {
        //! * 7 so that it matches with section position
        this.pathLength = Math.ceil(length * 7);
        this.updateFullPath(doNotUpdatePath);
    }

    public scrollGeometry: GeometryVector = EMPTY_GEOMETRY;

    //! Needed for background layers positionning
    public progressInUnit = 0;

    public setGeometryFromPercentage(perc: number) {
        this.progressInUnit = perc * this.pathLength;
        this.scrollGeometry = this.getScrollPahtWithAlignment(this.progressInUnit);
    }

    private getScrollPahtWithAlignment(unit: number): GeometryVector {
        const scrollPath = this.scrollPathFunction(unit);
        const { position, rotation } = scrollPath;
        rotation.addInPlace(this.alignmentVector);
        // const rotQuart = Quaternion.RotationAxis(this.alignmentVector, Math.PI / 2);
        // rotation.rotateByQuaternionAroundPointToRef(rotQuart, Vector3.Zero(), rotation);
        return { position, rotation };
    }

    public getGeometryFromPercentage(percentage): GeometryVector {
        const unit = percentage * this.pathLength;
        return this.getScrollPahtWithAlignment(unit);
    }
}
